import UserProfile from '../../features/user-profile/components/UserProfile';
import useIsMobileView from '../../hooks/useIsMobileView';
import UserProfileMobile from '../../features/user-profile/components/UserProfileMobile';
import Header from '../../components/header/Header';
import FriendsTable from './components/FriendsTable';
import { useAccountAbstractionInitialization } from '@/features/account-abstraction/hooks/useAccountAbstractionInitialization';
import InputSearch from '@/components/InputSearch/InputSearch';
import Button, { ButtonSizes, ButtonVariants } from '@/components/new-design/Button';
import { useNavigate } from 'react-router-dom';
import ROUTES from '@/routers/Routes';
import useGetFriendsRequest from '@/services/friends/queries/useGetFriendRequest';
import React, { useState, useEffect } from 'react';

import AddFriendModal from '@/pages/friends/components/AddFriendModal';
import useGetFriends from '@/services/friends/queries/useGetFriends';
import { Friend, FriendTablesSeatsPreview } from '@/services/friends/friendsServicesTypes';
import SendModalContainer from '@/features/user-profile/components/SendModalContainer';
import { IoArrowBackOutline } from 'react-icons/io5';
import FriendTablesModal from '@/pages/friends/components/FriendTablesModal';
import useWindowFocus from '@/hooks/useWindowFocus';
import BREAKPOINTS from '@/common/constant/breakpoints';

const Friends = () => {
  const [showAddFriendModal, setShowAddFriendModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showFriendsTableModal, setShowFriendsTableModal] = useState(false);
  const [selectedTablesToShow, setSelectedTablesToShow] = useState<FriendTablesSeatsPreview[]>([]);
  const [showTablesFriendId, setShowTablesFriendId] = useState('');
  const [friendTransferId, setFriendTransferId] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [filteredFriends, setFilteredFriends] = useState<Friend[]>([]);
  const isMobileView = useIsMobileView({ checkForUserAgent: false, breakpoint: BREAKPOINTS.xl });
  useAccountAbstractionInitialization();
  const navigate = useNavigate();
  const { data: friendsRequestData } = useGetFriendsRequest();
  const { data: friendsData, isFetching, isRefetching, refetch } = useGetFriends();

  const isFocused = useWindowFocus();

  useEffect(() => {
    if (isFocused) {
      refetch();
    }
  }, [isFocused, refetch]);

  useEffect(() => {
    if (friendsData?.data) {
      const filtered = friendsData.data.filter(
        (friend) =>
          friend.alias.toLowerCase().includes(searchValue.toLowerCase()) ||
          friend.firstName.toLowerCase().includes(searchValue.toLowerCase()) ||
          friend.lastName.toLowerCase().includes(searchValue.toLowerCase()),
      );
      setFilteredFriends(filtered);
    }
  }, [searchValue, friendsData]);

  const onClickAddNewFriendHandler = () => {
    setShowAddFriendModal(true);
  };

  const onClickFriendRequestHandler = () => navigate(ROUTES.FRIENDS_REQUEST);

  const onChangeInputHandler = (value: string) => {
    setSearchValue(value);
  };

  const onClickGoBackHandler = () => navigate(ROUTES.LOBBY);

  const friendRequestCount = friendsRequestData?.data.received.length;

  const onClickShowTablesHandler = (friendId: string) => {
    setShowTablesFriendId(friendId);
    setShowFriendsTableModal(true);
  };

  const onCloseFriendsTableModal = () => {
    setShowFriendsTableModal(false);
    setShowTablesFriendId('');
  };

  useEffect(() => {
    if (showTablesFriendId) {
      const selectedFriend = friendsData?.data.find((friend) => friend.id === showTablesFriendId);

      setSelectedTablesToShow(selectedFriend?.seats || []);
    }
  }, [showTablesFriendId, friendsData]);

  return (
    <>
      <div className="">
        <Header />
        {isMobileView && (
          <div className="mx-4">
            <UserProfileMobile />
          </div>
        )}
        <div className="pt2 flex h-full w-full gap-5 px-4 md:px-10 md:pt-5">
          <div className="flex w-full flex-col gap-4">
            <div className="flex w-full flex-wrap items-center gap-4">
              <IoArrowBackOutline onClick={onClickGoBackHandler} className="h-8 w-8 cursor-pointer text-white" />
              <span className="font-manrope text-2xl font-bold text-white">Friends</span>
            </div>
            <div className="flex flex-col gap-4 md:flex-row md:items-center">
              <div className="md:w-1/2 md:min-w-[370px]">
                <InputSearch fullWidth placeholder="Search for a friend..." handleNameSearch={onChangeInputHandler} />
              </div>
              <div className="flex gap-4">
                <>
                  <Button
                    size={ButtonSizes.md}
                    variant={ButtonVariants.pokerTable}
                    className="!h-11 w-48 px-3 !text-sm"
                    onClick={onClickFriendRequestHandler}
                  >
                    {friendRequestCount} Friend Requests
                  </Button>
                  <Button
                    variant={ButtonVariants.primary}
                    className="!h-11 w-40 px-3 !text-sm"
                    onClick={onClickAddNewFriendHandler}
                  >
                    Add New Friend
                  </Button>
                </>
              </div>
            </div>
            <FriendsTable
              onClickShowTables={onClickShowTablesHandler}
              onClickTransfer={(friend) => {
                setFriendTransferId(friend);
                setShowTransferModal(true);
              }}
              isFetching={isFetching}
              isRefetching={isRefetching}
              data={filteredFriends}
              refetch={refetch}
            />
          </div>
          {!isMobileView && <UserProfile />}
        </div>
      </div>
      <AddFriendModal
        refetch={refetch}
        sentRequest={friendsRequestData?.data.sent || []}
        open={showAddFriendModal}
        onClose={() => setShowAddFriendModal(false)}
      />
      <SendModalContainer
        defaultTransferId={friendTransferId}
        defaultTabIndex={1}
        open={showTransferModal}
        setClose={() => setShowTransferModal(false)}
      />
      <FriendTablesModal
        isFetching={isFetching}
        tables={selectedTablesToShow}
        isOpen={showFriendsTableModal}
        onClose={onCloseFriendsTableModal}
      />
    </>
  );
};

export default Friends;
